import { useState, useEffect, useCallback } from 'react';
import { userService } from '../services/user.service';
import { useToast } from '../components/common/Toast';
import { validateAddress } from '../utils/addressValidator';

export const useAddresses = () => {
  const toast = useToast();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAddresses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userService.getAddresses();
      setAddresses(response.data.addresses || []);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch addresses';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const addAddress = useCallback(async (data) => {
    const validation = validateAddress(data);
    
    if (!validation.isValid) {
      return { success: false, errors: validation.errors };
    }

    setLoading(true);
    try {
      const response = await userService.addAddress(data);
      await fetchAddresses();
      toast.success('Address added successfully');
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to add address';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [toast, fetchAddresses]);

  const updateAddress = useCallback(async (id, data) => {
    const validation = validateAddress(data);
    
    if (!validation.isValid) {
      return { success: false, errors: validation.errors };
    }

    setLoading(true);
    try {
      const response = await userService.updateAddress(id, data);
      await fetchAddresses();
      toast.success('Address updated successfully');
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update address';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [toast, fetchAddresses]);

  const deleteAddress = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return { success: false };
    }

    setLoading(true);
    try {
      await userService.deleteAddress(id);
      await fetchAddresses();
      toast.success('Address deleted successfully');
      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete address';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [toast, fetchAddresses]);

  const setDefaultAddress = useCallback(async (id) => {
    setLoading(true);
    try {
      await userService.setDefaultAddress(id);
      await fetchAddresses();
      toast.success('Default address updated');
      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to set default address';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [toast, fetchAddresses]);

  return {
    addresses,
    loading,
    error,
    fetchAddresses,
    addAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
  };
};

export default useAddresses;